import { createFileRoute, Link, useSearch, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useRef, type FormEvent } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { TackLogo } from "@/components/TackLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Gift, Mail } from "lucide-react";
import { toast } from "sonner";
import { SiteFooter } from "@/components/SiteFooter";
import { trackPlausible } from "@/lib/plausible";
import { validateReferralCodeFn } from "@/lib/referral.functions";

type SignupSearch = { ref?: string };

export const Route = createFileRoute("/signup")({
  head: () => ({
    meta: [
      { title: "Tack. Skapa konto" },
      { name: "description", content: "Skapa ditt Tack-konto och få fler kunder att boka igen. 30 dagar gratis på Solo." },
      { property: "og:title", content: "Tack. Skapa konto" },
      { property: "og:description", content: "Skapa ditt Tack-konto och få fler kunder att boka igen. 30 dagar gratis på Solo." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://tack.company/signup" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://tack.company/signup" }],
  }),
  component: SignupPage,
  validateSearch: (search: Record<string, unknown>): SignupSearch => ({
    ref: typeof search.ref === "string" ? search.ref : undefined,
  }),
});

function normalizeRef(s: string) {
  return s.toUpperCase().replace(/[^A-Z2-9]/g, "").slice(0, 6);
}

function SignupPage() {
  const { signup, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: "/signup" });
  const [fullName, setFullName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [countryCode, setCountryCode] = useState("SE");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);

  const [refStatus, setRefStatus] = useState<
    | { state: "idle" }
    | { state: "checking" }
    | { state: "valid"; businessName: string }
    | { state: "invalid" }
  >({ state: "idle" });

  useEffect(() => {
    if (search.ref) setReferralCode(normalizeRef(search.ref));
  }, [search.ref]);

  // Redirect already-signed-in users away from signup
  const redirectedRef = useRef(false);
  useEffect(() => {
    if (isAuthenticated && !pendingEmail && !redirectedRef.current) {
      redirectedRef.current = true;
      navigate({ to: "/dashboard" });
    }
  }, [isAuthenticated, pendingEmail, navigate]);

  // Debounced validation against the database
  useEffect(() => {
    if (referralCode.length < 6) {
      setRefStatus({ state: "idle" });
      return;
    }
    setRefStatus({ state: "checking" });
    const handle = setTimeout(async () => {
      try {
        const res = await validateReferralCodeFn({ data: { code: referralCode } });
        if (res.valid) {
          setRefStatus({ state: "valid", businessName: res.businessName });
        } else {
          setRefStatus({ state: "invalid" });
        }
      } catch {
        setRefStatus({ state: "invalid" });
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [referralCode]);

  // Resend cooldown ticker
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  const hasValidRef = refStatus.state === "valid";

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error(t('auth.passwordMin6'));
      return;
    }
    setLoading(true);
    trackPlausible("Signup Started", { has_referral: hasValidRef });
    try {
      // Stash business name + referral code in user_metadata so /auth/callback
      // can complete setup even if the user verifies in a different tab/device.
      const extraMeta: Record<string, unknown> = {};
      if (businessName) extraMeta.business_name = businessName;
      if (countryCode) extraMeta.country_code = countryCode;
      if (hasValidRef) extraMeta.referral_code = referralCode;

      await signup(email, password, fullName, extraMeta);
      trackPlausible("Signup Completed", { has_referral: hasValidRef });

      // With auto_confirm_email off, signUp returns session:null. Show the
      // "check your inbox" screen; merchant creation happens in /auth/callback.
      setPendingEmail(email);
      setResendCooldown(30);
    } catch (err: any) {
      toast.error(err.message || t('auth.signupFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!pendingEmail || resendCooldown > 0) return;
    setResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: pendingEmail,
        options: {
          emailRedirectTo:
            typeof window !== "undefined" ? `${window.location.origin}/auth/callback` : undefined,
        },
      });
      if (error) throw error;
      toast.success(t('auth.resendSent'));
      setResendCooldown(30);
    } catch (err: any) {
      toast.error(err.message || t('auth.resetSendFailed'));
    } finally {
      setResending(false);
    }
  };

  if (pendingEmail) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-sm">
            <div className="text-center mb-8">
              <TackLogo size="lg" />
            </div>
            <Card>
              <CardHeader className="text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="font-display">{t('auth.checkEmailTitle')}</CardTitle>
                <CardDescription>
                  <Trans
                    i18nKey="auth.checkEmailBody"
                    values={{ email: pendingEmail }}
                    components={{ 1: <span className="font-semibold text-foreground" /> }}
                  />
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  type="button"
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || resending}
                  className="w-full"
                  variant="outline"
                >
                  {resendCooldown > 0
                    ? t('auth.resendCooldown', { seconds: resendCooldown })
                    : resending
                    ? t('auth.sending')
                    : t('auth.resendEmail')}
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setPendingEmail(null);
                    setEmail("");
                    setPassword("");
                  }}
                  className="w-full text-sm text-muted-foreground hover:text-foreground"
                >
                  {t('auth.useDifferentEmail')}
                </button>
              </CardContent>
            </Card>
          </div>
        </div>
        <SiteFooter variant="legal" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <TackLogo size="lg" />
          </div>
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="font-display">{t('auth.createTitle')}</CardTitle>
              <CardDescription>{t('auth.createDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
              {refStatus.state === "valid" && (
                <div className="mb-4 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 flex items-center gap-2">
                  <Gift size={16} className="text-primary shrink-0" />
                  <p className="text-xs">
                    <Trans
                      i18nKey="auth.referralApplied"
                      values={{ code: referralCode, business: refStatus.businessName }}
                      components={{ 1: <span className="font-mono font-semibold" />, 3: <span className="font-semibold" /> }}
                    />
                  </p>
                </div>
              )}
              {refStatus.state === "checking" && (
                <div className="mb-4 rounded-lg border border-border bg-muted/40 px-3 py-2">
                  <p className="text-xs text-muted-foreground">{t('auth.checkingReferral')}</p>
                </div>
              )}
              {refStatus.state === "invalid" && (
                <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
                  <p className="text-xs text-destructive">
                    {t('auth.referralInvalid', { code: referralCode })}
                  </p>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">{t('auth.yourName')}</Label>
                  <Input id="fullName" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="businessName">{t('auth.businessName')}</Label>
                  <Input id="businessName" value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder={t('auth.businessPlaceholder')} required maxLength={25} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">{t('auth.country')}</Label>
                  <Select value={countryCode} onValueChange={setCountryCode}>
                    <SelectTrigger id="country">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SE">🇸🇪 {t('auth.countrySweden')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">{t('auth.countryHelp')}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth.email')}</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">{t('auth.password')}</Label>
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="referralCode" className="flex items-center gap-1.5">
                    {t('auth.referralCode')} <span className="text-xs text-muted-foreground font-normal">{t('auth.optional')}</span>
                  </Label>
                  <Input
                    id="referralCode"
                    value={referralCode}
                    onChange={(e) => setReferralCode(normalizeRef(e.target.value))}
                    placeholder="e.g. K7M3X9"
                    maxLength={6}
                    className="font-mono uppercase tracking-wider"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? t('auth.creating') : t('auth.createBtn')}
                </Button>
                <p className="text-center text-xs text-muted-foreground">
                  {t('landing.trialReassurance')}
                </p>
                <p className="text-center text-sm text-muted-foreground">
                  {t('auth.alreadyHave')}{" "}
                  <Link to="/login" className="text-primary hover:underline">{t('auth.signInLink')}</Link>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
      <SiteFooter variant="legal" />
    </div>
  );
}
